import { TEST_IDS } from '@va/constants';
import { Clock } from '@va/icons';
import { useTranslate } from '@va/localization';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import classNames from 'classnames';
import { forwardRef, useRef, useState } from 'react';

import { DatePickerContextProvider, DatePickerContextProviderProps, useDatePickerContext } from './context';
import { DatePickerTooltip } from './DatePickerTooltip';
import { PlacementType } from './types';

import './date-picker.css';

export type DatePickerV2Props = DatePickerContextProviderProps & {
  placement?: PlacementType;
};

const DatePickerContent = forwardRef<HTMLButtonElement>((_, ref) => {
  const translate = useTranslate();
  const tooltipContext = useTooltipContext();
  const { size, overlay, onHover, onClick, prefixValue, pickerValue, placeholder } = useDatePickerContext();

  return (
    <button
      ref={ref}
      id='litepie'
      data-testid={TEST_IDS.specific.datePicker.toggleBtn}
      className={classNames('h-full relative overflow-hidden w-fit bg-white-snow! group', {
        'litepie-datepicker-overlay': overlay,
        'rounded-9px': size === 'small',
        'rounded-12': size === 'medium',
      })}
      onClick={(e) => {
        e.preventDefault();
        tooltipContext.context.onOpenChange(!tooltipContext.context.open);
        onClick?.();
      }}
      onMouseEnter={onHover}
    >
      <label className='h-full relative block -outline-offset-1'>
        <input
          type='text'
          tabIndex={-1}
          className={classNames(
            'absolute cursor-pointer h-full w-full block pl-12 pr-3 py-2.5 overflow-hidden text-sm placeholder-primary transition-colors bg-transparent group-focus:outline-solid group-focus:outline-[1.5px] outline-black-lighter-30 -outline-offset-1',
            {
              'rounded-9px': size === 'small',
              'rounded-12': size === 'medium',
            },
          )}
          readOnly
        />
        <span
          className={classNames(
            'flex items-center gap-5 justify-between cursor-pointer border-gray-concrete border-2 h-full relative pl-10 sm:pl-8 pr-9px py-9px text-sm placeholder-primary transition-colors border-none',
            {
              'rounded-9px xs:block': size === 'small',
              'rounded-12 min-h-12': size === 'medium',
              'sm:block': size === 'medium' && prefixValue,
            },
          )}
        >
          <span className={'text-15 font-semibold leading-6 empty:hidden sm:hidden'}>
            {prefixValue && translate(prefixValue.transKey, { interval: prefixValue.diff })}
          </span>
          <span className='flex items-center grow overflow-hidden'>
            <span className='text-12 font-medium leading-18'>
              {pickerValue ?? placeholder ?? translate('datepicker.placeholder')}
            </span>
            <DropdownArrow
              className='ml-initial'
              iconClassName={classNames('ml-0.5', {
                'w-6px': size === 'small',
                'w-8px': size === 'medium',
              })}
              open={tooltipContext.context.open}
            />
          </span>
        </span>
        <span className='absolute inset-y-0 left-0 inline-flex items-center rounded-md overflow-hidden'>
          <div
            className={classNames('px-1.5 py-1 focus:outline-hidden text-primary-400 rounded-md', {
              'ml-2': size === 'medium',
              'ml-1': size === 'small',
            })}
          >
            <Clock strokeColor='#3C3C3C' className='w-3.75' />
          </div>
        </span>
      </label>
    </button>
  );
});

export const DatePickerV2 = ({ placement, ...props }: DatePickerV2Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <DatePickerContextProvider {...props}>
      <TooltipWrapper
        useDefaultStyle={false}
        placement={placement}
        interactive
        arrow={false}
        trigger='click'
        content={<DatePickerTooltip />}
        shiftCrossAxis
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) buttonRef.current?.focus();
          setIsOpen(open);
        }}
      >
        <DatePickerContent ref={buttonRef} />
      </TooltipWrapper>
    </DatePickerContextProvider>
  );
};
